<script setup>
import { ref } from 'vue'
import { $$t } from '@/utils/i19m'
import { storeToRefs } from 'pinia'

import { usePageBuilder } from '~/stores/page-builder'
import { useUrlBuilder } from '~/stores/url-builder'
import { useSignalrBuilder } from '~/stores/signalr-builder'
import { secondsToISO8601 } from '~~/utils/secondsToISO8601'
import Player from '@/models/player/Player'

const urlBuilderStore = useUrlBuilder()
const pageBuilderStore = usePageBuilder()

const props = defineProps({
  ssrPageData: {
    type: Object,
    required: false,
    default: null,
  },
})

const { isCLH, getProjecDomain } = urlBuilderStore
const { deviceType, getProjectFavIcon } = storeToRefs(pageBuilderStore)
const signalrBuilderStore = useSignalrBuilder()

// const { realTimeMediaViews } = storeToRefs(signalrBuilderStore);
const mediaData = ref(null)

const scriptsToLoadEachPage = ref(null)
const scriptsToLoadOnce = ref(null)
const analitycsPropertiesData = ref(null)
const seoData = ref(null)

const { path, query, fullPath } = useRoute()

let response
try {
  if (process.server) {
    response = await pageBuilderStore.actionGetLayoutData(
      path,
      true,
      props.ssrPageData
    )
  } else {
    response = await pageBuilderStore.actionGetLayoutData(path)
  }
} catch (error) {
  console.error(error)
} finally {
  const {
    customScriptsLoadEachPage,
    customScriptsLoadOnce,
    analitycsData,
    seo,
    media,
  } = response

  scriptsToLoadEachPage.value = customScriptsLoadEachPage
  scriptsToLoadOnce.value = customScriptsLoadOnce
  analitycsPropertiesData.value = analitycsData
  seoData.value = seo
  mediaData.value = media.model
}

const media = ref(null)
const videoPlayerUrl = ref(null)
const videoPlayerConfig = ref(null)

const externalVideoId = ref(null)

const mediaId = ref(null)

const isLive = ref(false)

const isPrivateMedia = ref(false)
const playerLoaded = ref(false)
const playerData = ref(new Player())

const loadPlayer = () => {
  if (isPrivateMedia.value) return

  playerLoaded.value = true
  setupPlayer(playerData.value.data)

  const player = vpPlayer(`player-element`)

  player.on('video-state', (event) =>
    vpanalytics.notifyHandlers('onVideoState', event)
  )

  player.on('vp-event', async (e) => {
    if (e.eventName == 'firstFrame')
      vpanalytics.notifyHandlers('onFirstFramePlay')

    if (e.eventName == 'video-completed') vpanalytics.notifyHandlers('onEnded')

    if (e.eventName == 'video-started') vpanalytics.notifyHandlers('onPlay')

    if (e.eventName == 'firstQuartile')
      vpanalytics.notifyHandlers('onFirstQuartilePlay')

    if (e.eventName == 'secondQuartile')
      vpanalytics.notifyHandlers('onSecondQuartilePlay')

    if (e.eventName == 'thirdQuartile')
      vpanalytics.notifyHandlers('onThirdQuartilePlay')

    if (e.eventName == 'video-state')
      vpanalytics.notifyHandlers('videoState', e)

    if (e.eventName == 'vp-video-switch')
      vpanalytics.notifyHandlers('onVideoSwitch', e)

    if (e.eventName == 'watchedtime')
      vpanalytics.notifyHandlers('onWatchedTime', e)
  })
}

if (mediaData.value) {
  media.value = mediaData.value.model
  mediaId.value = mediaData.value.id
}

videoPlayerUrl.value = mediaData.value?.assets
  ? mediaData.value?.assets[0]?.rootUrl
  : null

externalVideoId.value = mediaData.value?.externalMediaId

isLive.value = mediaData.value?.isLive

if (mediaData.value && mediaData.value.embedPlayer?.config) {
  if (mediaData.value.lockedAfter && mediaData.value.lockedAfter > 0) {
    mediaData.value.embedPlayer.config.video.videoLocking = {
      isEnabled: true,
      value: mediaData.value.lockedAfter,
    }
  }
  videoPlayerConfig.value = mediaData.value.embedPlayer.config
} else if (mediaData.value && mediaData.value.player?.config) {
  videoPlayerConfig.value = mediaData.value.player.config
} else {
  isPrivateMedia.value = true
}

if (videoPlayerConfig.value && !isPrivateMedia.value) {
  videoPlayerConfig.value.video.thumbnail = mediaData.value.imageAssets
    ? mediaData.value.imageAssets[mediaData.value.imageAssets.length - 1]?.url
    : null
}

const setupPlayer = (data) => {
  vpPlayer(`player-element`).setup(data)
}

onNuxtReady(() => {
  window.__VPAPPS__ = analitycsPropertiesData.value

  useHead({
    script: scriptsToLoadEachPage.value,
  })

  if (window.vpanalytics) {
    vpanalytics.notifyHandlers('onPageView')
    vpanalytics.notifyHandlers('onWebLocation')
  }
})

function injectVideoObject() {
  const rawDate = mediaData.value?.startDate
  const uploadDate = rawDate
    ? new Date(rawDate).toISOString()
    : '2024-12-10T08:00:00Z'

  const videoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: seoData.value?.title || 'Default Video Title',
    description: seoData.value?.description || 'Default Video Description',
    thumbnailUrl:
      seoData.value?.metaOg?.image ||
      'https://example.com/default-thumbnail.jpg',
    uploadDate: uploadDate,
    duration: secondsToISO8601(mediaData.value?.length) || 'PT2M33S',
    embedUrl: `${window.location.origin}${path}`,
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: mediaData.value?.viewCount || 0,
    },
  }
  const scriptTag = document.createElement('script')
  scriptTag.type = 'application/ld+json'
  scriptTag.classList.add('structured-data') // Add class to identify later
  scriptTag.setAttribute('data-page', fullPath) // Add page identifier
  scriptTag.innerHTML = JSON.stringify(videoStructuredData)
  document.head.appendChild(scriptTag)
}

onMounted(() => {
  // const playerData = new Player();
  playerData.value.fromData(
    videoPlayerUrl,
    videoPlayerConfig.value,
    externalVideoId.value
  )

  injectVideoObject()

  // console.error('PLAYER DATA', videoPlayerUrl,
  //   videoPlayerConfig.value,
  //   externalVideoId.value)

  playerData.value.data.config.autostartOnLoad = {
    state: true,
    onMobile: true,
    onData: true,
  }

  playerData.value.data.config.muted = true
  playerData.value.data.video.live = isLive.value

  try {
    if (query?.autoplay) {
      playerData.value.data.config.autostartOnLoad.state = JSON.parse(
        query.autoplay.toLowerCase()
      )
    }
    if (!query?.autoplay && query?.am && deviceType === 'mobile') {
      playerData.value.data.config.autostartOnLoad.state =
        playerData.value.data.config.autostartOnLoad.onMobile = JSON.parse(
          query?.am.toLowerCase()
        )
    }
    if (query?.muted || query?.am)
      if (query.muted) {
        playerData.value.data.config.muted = JSON.parse(
          query.muted.toLowerCase()
        )
      }

    if (query?.t) {
      playerData.value.data.video.startTime = Number(query.t)
    }

    if (query.autoplay === 'true' || query.am === 'true') {
      loadPlayer()
    }
  } catch (error) {
    console.error(error)
  }

  if (isLive.value && mediaId.value) {
    signalrBuilderStore.setToJoinQueue(
      SIGNALR_CONNECTION.JOIN_REALTIME_VIEWS,
      mediaId.value
    )
  }
})

function cleanUpScripts() {
  // Find and remove scripts
  const scriptTags = document.querySelectorAll(
    `script.load-every-time-script[data-page="${fullPath}"]`
  )
  scriptTags.forEach((scriptTag) => {
    scriptTag.parentNode.removeChild(scriptTag)
  })

  const structuredDataScripts = document.querySelectorAll(
    `script.structured-data[data-page="${fullPath}"]`
  )
  structuredDataScripts.forEach((scriptTag) => {
    scriptTag.parentNode.removeChild(scriptTag)
  })
}

onBeforeUnmount(() => {
  try {
    vpPlayer(`player-element`).destroy()
  } catch (error) {
    console.error(error)
  }

  cleanUpScripts()

  if (isLive.value && mediaId.value) {
    signalrBuilderStore.leaveGroup(mediaId.value)
  }
})

onUnmounted(() => {
  cleanUpScripts()
})
</script>

<template>
  <div class="embed-page bg-black">
    <!-- <pre class="text-white">
        {{ getAnalyticsProperties }}
      </pre> -->

    <Head v-if="seoData">
      <Meta
        v-if="seoData.robotRules"
        name="robots"
        :content="seoData.robotRules"
      />

      <Link rel="dns-prefetch" href="https://cdn.vpapps.cloud/" />

      <Link rel="icon" :href="getProjectFavIcon" />

      <Title>{{ seoData?.title }}</Title>
      <Meta name="description" :content="seoData?.description" />
      <Meta name="author" :content="seoData?.author" />

      <Meta property="og:title" :content="seoData?.metaOg?.title" />
      <Meta property="og:description" :content="seoData?.metaOg?.description" />
      <Meta property="og:image" :content="seoData?.metaOg?.image" />

      <Meta property="twitter:title" :content="seoData?.twitterCard?.title" />
      <Meta
        property="twitter:description"
        :content="seoData?.twitterCard?.description"
      />
      <Meta property="twitter:image" :content="seoData?.twitterCard?.image" />
    </Head>

    <Head v-if="isPrivateMedia">
      <Title>{{ $$t('component_player_private_media') }}</Title>
    </Head>

    <div
      v-if="!playerLoaded"
      class="cursor-pointer mx-auto flex justify-center items-center w-full max-w-full"
      @click="loadPlayer"
      :style="{
        aspectRatio: '16/9',
        'background-image':
          videoPlayerConfig && videoPlayerConfig.video.thumbnail
            ? `url(${videoPlayerConfig.video.thumbnail}?q=100)`
            : '',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }"
    >
      <div class="flex flex-col justify-center items-center">
        <div
          class="w-16 h-16 bg-[#c00] flex justify-center items-center rounded-full"
        >
          <span class="vpapps-font-play text-4xl text-white" />
        </div>
        <div v-if="isPrivateMedia" class="text-white mt-4">
          {{ $$t('component_player_private_media') }}
        </div>
      </div>
    </div>

    <div
      v-show="playerLoaded"
      itemscope
      itemtype="https://schema.org/VideoObject"
    >
      <meta itemprop="uploadDate" :content="media?.startDate" />
      <meta itemprop="name" />
      <meta itemprop="duration" :content="media?.duration" />
      <meta
        itemprop="thumbnailUrl"
        :content="videoPlayerConfig?.video?.thumbnail"
      />
      <meta itemprop="contentUrl" :content="media?.assets?.[0]?.rootUrl" />
      <div
        id="player-element"
        :style="{
          position: 'relative',
          overflow: 'hidden',
          aspectRatio: '16/9',
          'background-image':
            isCLH &&
            media.imageAssets[0].url &&
            media.imageAssets[0].url.length > 0
              ? `url(${videoPlayerConfig.video?.thumbnail})`
              : '',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }"
      >
        <video></video>
      </div>
    </div>
    <!-- </ClientOnly> -->
  </div>
</template>

<style></style>
